import { FormControlLabel, Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { TypePersistence } from 'civil-status/constants/type-section';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { Checkbox } from '@mui/material';
import { ItemVM } from 'shell/view-models/item';
import ListParentRecognition from '../list/list-parent-recognition';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import ListAnsc from '../../../../../../shared/list-ansc';

interface GeneralDataRecognitionProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  setDataInterprete: (isWithInterprete?: boolean) => void;
  controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence: (
    typologyConsentAndConfiguration?: ItemVM,
    typeDeclaration?: string,
    parentRecognition?: string,
    isIncestuousSonSentence?: boolean
  ) => void;
  configuration?: string;
}

const GeneralDataRecognitionComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  setDataInterprete,
  controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence,
  configuration,
}: GeneralDataRecognitionProps): React.ReactElement => {
  const handleListPresentationDocument = useEventCallback((item?: ItemVM) => {
    onChange('presentationDocument', item?.id);
  });

  const handleListTypologyConsent = useEventCallback((item?: ItemVM) => {
    onChange('typologyConsent', item?.id);
  });

  const handleListTypeDeclaration = useEventCallback((item?: ItemVM) => {
    onChange('typeDeclaration', item?.id);
  });

  const handleListChangeSurname = useEventCallback((item?: ItemVM) => {
    onChange('changeSurname', item?.id);
  });

  const handleListParentRecognition = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('parentRecognition', event.target.value);
  });

  useEffect(() => {
    controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence({
      id: configuration || '',
      value: values?.typologyConsent || '',
    });
  }, [values?.typologyConsent]);

  useEffect(() => {
    controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence(
      undefined,
      values?.typeDeclaration
    );
  }, [values?.typeDeclaration]);

  useEffect(() => {
    controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence(
      undefined,
      undefined,
      values?.parentRecognition
    );
  }, [values?.parentRecognition]);

  useEffect(() => {
    controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence(
      undefined,
      undefined,
      undefined,
      values?.isIncestuousSonSentence
    );
  }, [values?.isIncestuousSonSentence]);

  useEffect(() => {
    setDataInterprete(values?.isWithInterprete);
  }, [values?.isWithInterprete]);

  const isRicDgcSas = useMemo(() => {
    return configuration === ConfigurationType.RICDGCSAS;
  }, [configuration]);

  const isRicDgcs = useMemo(() => {
    return configuration === ConfigurationType.RICDGCS;
  }, [configuration]);

  const isRicDgp = useMemo(() => {
    return configuration === ConfigurationType.RICDGP;
  }, [configuration]);

  const isRicCons = useMemo(() => {
    return configuration === ConfigurationType.RICCONS;
  }, [configuration]);

  const isRicAsse = useMemo(() => {
    return configuration === ConfigurationType.RICASSE;
  }, [configuration]);

  const isRicDgcSasc = useMemo(() => {
    return configuration === ConfigurationType.RICDGCSASC;
  }, [configuration]);

  const isRicDgcSse = useMemo(() => {
    return configuration === ConfigurationType.RICDGCSSE;
  }, [configuration]);

  const isUcdtsp02 = useMemo(() => {
    return configuration === ConfigurationType.UCDTSP06;
  }, [configuration]);

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={6}>
        <ListAnsc
          label="Presentazione documenti"
          idDominio="36"
          value={values?.presentationDocument || ''}
          onChange={handleListPresentationDocument}
          disabled={disabled}
          error={Boolean(errors?.presentationDocument) && touched?.presentationDocument}
          required
        />
      </Grid>
      {isRicDgcSas || isRicDgcs || isRicDgcSasc || isRicDgcSse ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label={
              values?.typePersistence === TypePersistence.DATI_EVENTO_RICONOSCIMENTO_M ||
              values?.typePersistence === TypePersistence.EVENTO_RICONOSCIMENTO_CONSENSO_M
                ? 'Tipologia consenso madre'
                : 'Tipologia consenso padre'
            }
            idDominio="12"
            value={values?.typologyConsent || ''}
            onChange={handleListTypologyConsent}
            disabled={disabled}
            error={Boolean(errors?.typologyConsent) && touched?.typologyConsent}
            required
          />
        </Grid>
      ) : undefined}
      {isRicDgp || isRicDgcs || isRicDgcSse || isRicAsse || isRicCons || isUcdtsp02 ? undefined : (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Tipo dichiarazione assenso"
            idDominio="37"
            value={values?.typeDeclaration || ''}
            onChange={handleListTypeDeclaration}
            disabled={disabled}
            error={Boolean(errors?.typeDeclaration) && touched?.typeDeclaration}
            required
          />
        </Grid>
      )}
      {isRicDgcSasc ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Scelta cambio cognome"
            idDominio="39"
            value={values?.changeSurname || ''}
            onChange={handleListChangeSurname}
            disabled={disabled}
            error={Boolean(errors?.changeSurname) && touched?.changeSurname}
            required
          />
        </Grid>
      ) : undefined}
      {isRicCons ? (
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data comunicazione *"
            aria-label="Data comunicazione"
            value={values?.dateCommunication || null}
            onChange={(date) => {
              onChange('dateCommunication', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateCommunication"
                fullWidth
                helperText={errors?.dateCommunication}
                error={touched?.dateCommunication && Boolean(errors?.dateCommunication)}
              />
            )}
          />
        </Grid>
      ) : undefined}
      {isRicDgcSse ? (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isIncestuousSonSentence"
                checked={values?.isIncestuousSonSentence || false}
                onChange={() => {
                  onChange('isIncestuousSonSentence', !values?.isIncestuousSonSentence);
                }}
                inputProps={{ 'aria-label': 'Sentenza figlio incestuoso' }}
              />
            }
            label="Sentenza figlio incestuoso"
          />
        </Grid>
      ) : undefined}
      {isRicAsse ? (
        <Grid item xs={12} md={6}>
          <ListParentRecognition
            value={values?.parentRecognition || ''}
            onChange={handleListParentRecognition}
            disabled={disabled}
            error={Boolean(errors?.parentRecognition) && touched?.parentRecognition}
            required
            label="Riconoscimento genitore"
          />
        </Grid>
      ) : undefined}
      {isUcdtsp02 ? (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isWithInterprete"
                checked={values?.isWithInterprete || false}
                onChange={() => {
                  onChange('isWithInterprete', !values?.isWithInterprete);
                }}
                inputProps={{ 'aria-label': 'Presenza interprete' }}
                disabled={disabled}
              />
            }
            label="Presenza interprete"
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

GeneralDataRecognitionComponent.displayName = 'Form General Data Recognition component';

const GeneralDataRecognition = memo(GeneralDataRecognitionComponent);

export default GeneralDataRecognition;
